class HeaderDrawerChroxel extends HTMLElement {
  constructor() {
    super();

    this.toggle = document.querySelector('#header-drawer-toggle');
    this.closeButton = this.querySelector('.header-drawer-chroxel__close');
    this.panel = this.querySelector('.header-drawer-chroxel__sheet');
    this.panels = this.querySelector('.header-drawer-chroxel__panels');

    this._mobileBreakpoint = 749;
    this._pointerStartY = 0;
    this._isDragging = false;
    this._dragHandlersAttached = false;

    this.handleEscape = this.handleEscape.bind(this);
  }

  connectedCallback() {
    if (this.toggle) {
      this.toggle.addEventListener('click', () => {
        if (this.classList.contains('active')) {
          this.close();
        } else {
          this.open();
        }
      });
    }

    this.addEventListener('click', (event) => {
      if (event.target === this) this.close();
    });

    if (this.closeButton) {
      this.closeButton.addEventListener('click', () => this.close());
    }

    // Level 1: Accordion triggers
    this.accordionTriggers = this.querySelectorAll('[data-accordion-trigger]');
    this.accordionTriggers.forEach((trigger) => {
      trigger.addEventListener('click', () => {
        this.toggleAccordion(trigger.dataset.accordionTrigger);
      });
    });

    // Level 2: Expand children triggers
    this.expandTriggers = this.querySelectorAll('[data-expand-trigger]');
    this.expandTriggers.forEach((trigger) => {
      trigger.addEventListener('click', (e) => {
        e.stopPropagation();
        this.toggleChildren(trigger.dataset.expandTrigger);
      });
    });

    document.addEventListener('keydown', this.handleEscape);

    this.setupBottomSheet();
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.handleEscape);
    this.detachDragHandlers();
    if (this._mediaQuery && this._mediaQueryHandler) {
      this._mediaQuery.removeEventListener('change', this._mediaQueryHandler);
    }
  }

  open() {
    if (this.classList.contains('active')) return;
    if (this.toggle) this.activeElement = this.toggle;
    this.toggle?.setAttribute('aria-expanded', 'true');

    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        if (typeof trapFocus === 'function') {
          trapFocus(this, this.closeButton);
        }
      },
      { once: true },
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    if (!this.classList.contains('active')) return;
    this.classList.remove('active');
    this.toggle?.setAttribute('aria-expanded', 'false');
    this.collapseAllAccordions();

    if (typeof removeTrapFocus === 'function') {
      removeTrapFocus(this.activeElement);
    }
    document.body.classList.remove('overflow-hidden');
  }

  handleEscape(event) {
    if (event.key === 'Escape' && this.classList.contains('active')) {
      this.close();
    }
  }

  // Level 1 accordion toggle
  toggleAccordion(id) {
    const trigger = this.querySelector(`[data-accordion-trigger="${id}"]`);
    const content = this.querySelector(`#AccordionContent-${id}`);
    if (!trigger || !content) return;

    const isExpanded = trigger.getAttribute('aria-expanded') === 'true';

    // Close other accordions
    this.accordionTriggers.forEach((otherTrigger) => {
      if (otherTrigger !== trigger) {
        const otherId = otherTrigger.dataset.accordionTrigger;
        const otherContent = this.querySelector(`#AccordionContent-${otherId}`);
        if (otherContent) {
          otherContent.style.maxHeight = '0';
          otherContent.classList.remove('is-open');
        }
        otherTrigger.setAttribute('aria-expanded', 'false');
      }
    });

    trigger.setAttribute('aria-expanded', String(!isExpanded));

    if (isExpanded) {
      content.style.maxHeight = '0';
      content.classList.remove('is-open');
    } else {
      content.style.maxHeight = content.scrollHeight + 'px';
      content.classList.add('is-open');
    }
  }

  // Level 2 expand children toggle
  toggleChildren(id) {
    const trigger = this.querySelector(`[data-expand-trigger="${id}"]`);
    const children = this.querySelector(`#Children-${id}`);
    if (!trigger || !children) return;

    const isExpanded = trigger.getAttribute('aria-expanded') === 'true';
    trigger.setAttribute('aria-expanded', String(!isExpanded));

    if (isExpanded) {
      children.style.maxHeight = '0';
      children.classList.remove('is-open');
    } else {
      children.style.maxHeight = children.scrollHeight + 'px';
      children.classList.add('is-open');
    }
  }

  collapseAllAccordions() {
    this.accordionTriggers.forEach((trigger) => {
      trigger.setAttribute('aria-expanded', 'false');
    });
    this.querySelectorAll('.header-drawer-chroxel__accordion-content').forEach((content) => {
      content.style.maxHeight = '0';
      content.classList.remove('is-open');
    });
    this.expandTriggers.forEach((trigger) => {
      trigger.setAttribute('aria-expanded', 'false');
    });
    this.querySelectorAll('.header-drawer-chroxel__submenu-children').forEach((children) => {
      children.style.maxHeight = '0';
      children.classList.remove('is-open');
    });
  }

  setupBottomSheet() {
    if (!this.panel) return;
    if (window.matchMedia(`(max-width: ${this._mobileBreakpoint}px)`).matches) {
      this.attachDragHandlers();
    }
    this._mediaQuery = window.matchMedia(`(max-width: ${this._mobileBreakpoint}px)`);
    this._mediaQueryHandler = (event) => {
      if (event.matches) {
        this.attachDragHandlers();
      } else {
        this.detachDragHandlers();
      }
    };
    this._mediaQuery.addEventListener('change', this._mediaQueryHandler);
  }

  attachDragHandlers() {
    if (this._dragHandlersAttached || !this.panel) return;
    this._dragHandlersAttached = true;
    this.panel.addEventListener('pointerdown', this.onPointerDown);
  }

  detachDragHandlers() {
    if (!this._dragHandlersAttached) return;
    this._dragHandlersAttached = false;
    if (this.panel) {
      this.panel.removeEventListener('pointerdown', this.onPointerDown);
    }
    this.detachMoveHandlers();
  }

  detachMoveHandlers() {
    if (!this.panel) return;
    this.panel.removeEventListener('pointermove', this.onPointerMove);
    this.panel.removeEventListener('pointerup', this.onPointerEnd);
    this.panel.removeEventListener('pointercancel', this.onPointerEnd);
  }

  onPointerDown = (event) => {
    if (event.button !== 0) return;
    if (event.target.closest('button, a, input, select, textarea, [contenteditable], [role="button"]')) return;
    if (!this.classList.contains('active')) return;
    if (!this.panel) return;

    const rect = this.panel.getBoundingClientRect();
    const offsetFromTop = event.clientY - rect.top;
    if (offsetFromTop > 60) return;

    this._pointerStartY = event.clientY;
    this._isDragging = true;
    this.panel.setPointerCapture(event.pointerId);

    this.panel.addEventListener('pointermove', this.onPointerMove);
    this.panel.addEventListener('pointerup', this.onPointerEnd);
    this.panel.addEventListener('pointercancel', this.onPointerEnd);
  };

  onPointerMove = (event) => {
    if (!this._isDragging || !this.panel) return;
    const delta = event.clientY - this._pointerStartY;
    if (delta < 0) return;

    this.panel.style.transition = 'none';
    this.panel.style.transform = `translateY(${delta}px)`;
  };

  onPointerEnd = (event) => {
    if (!this._isDragging || !this.panel) return;
    this._isDragging = false;
    this.detachMoveHandlers();

    const delta = event.clientY - this._pointerStartY;
    const rect = this.panel.getBoundingClientRect();
    const threshold = Math.max(rect.height * 0.2, 100);

    this.panel.style.transition = '';
    this.panel.style.transform = '';

    if (delta > threshold) {
      this.close();
    }

    this._pointerStartY = 0;
  };
}

customElements.define('header-drawer-chroxel', HeaderDrawerChroxel);
